import { useEffect, useMemo, useState } from "react";
import cogoToast from "cogo-toast";
import { Badge, Button, Col, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import {
    useAcceptCorporateApplicationAgreementsMutation,
    useCreateCorporateAccountMutation,
    useCreateCorporateApplicationMutation,
    useDeleteCorporateApplicationPartyMutation,
    useGetCorporateApplicationAgreementsQuery,
    useGetCorporateApplicationEligibilityQuery,
    useGetCorporateApplicationPartiesQuery,
    useGetCorporateApplicationQuery,
    useGetCorporateApplicationsQuery,
    useGetCorporateW8beneQuery,
    useLazyGetCorporateApplicationTinQuery,
    useLazyLookupAdminKycQuery,
    useLazyLookupAdminUsersQuery,
    useManualVerifyCorporateCacMutation,
    usePreviewCorporateW8beneMutation,
    useSaveCorporateApplicationPartiesMutation,
    useSaveCorporateW8beneAnswersMutation,
    useSignCorporateW8beneMutation,
    useStartCorporateW8beneMutation,
    useSubmitCorporateW8beneMutation,
    useSyncCorporateApplicationProvisionMutation,
    useUpdateCorporateApplicationMutation,
    useUploadCorporateApplicationDocumentMutation,
    useVerifyCorporateCacMutation,
} from "../../../../../redux/apiSlice";
import {
    canEditCompany,
    canEditReps,
    companyLockReason,
    EditLockAlert,
    getAlpacaProvisionError,
    ProvisionErrorAlert,
    repsLockReason,
} from "../corporateEditRules";

/* ------------------------------------------------------------------ *
 *  Small shared helpers
 * ------------------------------------------------------------------ */

const prettyEnum = (value) => `${value ?? ""}`.replace(/_/g, " ").trim();

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

// Street address is one line in the form. Splitting it on commas turned
// "PLOT 84, Unity Crescent, ..." into several street lines on every save
// (and made it impossible to type a comma in the W-8BEN-E address).
const streetToText = (street) => (Array.isArray(street) ? street.join(", ") : `${street ?? ""}`);
const textToStreet = (text) => {
    const value = `${text ?? ""}`.trim();
    return value ? [value] : [];
};

// "" -> undefined, anything else -> number. `Number(x) || undefined` dropped real zeros.
const toNumberOrUndefined = (value) => {
    if (value === "" || value === null || value === undefined) return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

// Browsers block opening data: URLs in a new tab, so the PDF links did nothing.
const openBase64Pdf = (base64) => {
    try {
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        cogoToast.error("Couldn't open the PDF");
    }
};

const STATUS_VARIANT = { APPROVED: "success", PENDING: "warning", REJECTED: "danger" };

// Latest uploaded document of a type (and side), newest first.
const latestDocument = (documents, type, side) => (documents || [])
    .filter((doc) => doc?.type === type && (side === undefined || (doc?.side || null) === side))
    .sort((a, b) => new Date(b?.created || 0) - new Date(a?.created || 0))[0];

const UploadedStatus = ({ document }) => {
    if (!document) return <span className="text-muted font-size-sm">Not uploaded yet</span>;
    const status = `${document.status || ""}`.toUpperCase();
    return (
        <span className="font-size-sm">
            <Badge variant={STATUS_VARIANT[status] || "secondary"} className="mr-2">{prettyEnum(status) || "Uploaded"}</Badge>
            {document.document && /^https?:/i.test(document.document) && (
                <a href={document.document} target="_blank" rel="noreferrer">View</a>
            )}
        </span>
    );
};

// The plain <Form.Control type="file"> rendered invisibly in this theme, so the
// document rows only showed a disabled Upload button with nothing to click.
// Same pattern as Commercial Papers: a hidden input opened by a label styled
// as a button, with the chosen file name next to it.
const FilePicker = ({ id, file, onChange }) => (
    <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
        <input
            id={id}
            key={file ? "picked" : "empty"}
            type="file"
            className="d-none"
            accept="image/*,application/pdf"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
        <label htmlFor={id} className="btn btn-sm btn-light-primary mb-0 mr-3 text-nowrap">
            {file ? "Change file" : "Choose file"}
        </label>
        <span className="text-muted font-size-sm text-truncate" title={file?.name || ""}>
            {file?.name || "No file selected"}
        </span>
    </div>
);

const LEGAL_ENTITY_TYPES = [
    "banking_institution", "broker_or_dealer", "c_corporation", "foreign_financial_institution",
    "fund_or_hedge_fund", "general_partnership", "irrevocable_trust", "limited_liability_corporation",
    "limited_partnership", "revocable_trust", "s_corporation", "unincorporated", "other",
];
const INDUSTRIES = [
    "administration_or_public_relations", "agriculture", "architecture", "arts_or_film_or_music",
    "construction", "design", "education", "finance", "food_production", "government_or_public_services",
    "healthcare", "legal_services", "media_or_marketing", "oil_or_energy_or_mining",
    "pharmaceuticals_or_biochemicals", "real_estate", "retail_restaurants", "technology",
    "sports_or_sports_medicine_or_fitness", "tourism", "transport", "other",
];
const COMPANY_FUNDING_SOURCES = ["business_revenue", "asset_appreciation", "sales_of_assets", "investors", "other"];
const PARTY_FUNDING_SOURCES = [
    "employment_income", "investments", "inheritance", "business_income", "savings", "family", "other",
];
const INVESTMENT_OBJECTIVES = [
    "balance_preserve_wealth_with_growth", "generate_income", "growth", "market_speculation", "preserve_wealth",
];

const WIZARD_STEPS = [
    { key: "find", label: "1. Select Customer" },
    { key: "company", label: "2. Company (CAC)" },
    { key: "parties", label: "3. Parties" },
    { key: "party-docs", label: "4. Party Documents" },
    { key: "org-docs", label: "5. Org Documents" },
    { key: "agreements", label: "6. Agreements" },
    { key: "review", label: "7. Review & Handoff" },
    { key: "w8bene", label: "8. W-8BEN-E" },
    { key: "create-account", label: "9. Create Account" },
];

const EmptyState = ({ children }) => (
    <div className="py-10 text-center text-muted">{children}</div>
);

/* ------------------------------------------------------------------ *
 *  Step 1 — Find / create the corporate user + application
 * ------------------------------------------------------------------ */

const isDraftStatus = (application) =>
    `${application?.applicationStatus ?? application?.status ?? ""}`.trim().toUpperCase() === "DRAFT";
const applicationUserId = (application) => application?.userId ?? application?.user?.userId ?? null;

const FindOrCreateStep = ({ userGroup, selectedUser, setSelectedUser, applicationId, setApplicationId, onResumeApplication }) => {
    const [lookupField, setLookupField] = useState("email");
    const [lookupValue, setLookupValue] = useState("");
    const [accountName, setAccountName] = useState("");
    const [existingApplicationId, setExistingApplicationId] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [triggerUserLookup, { data: userLookupResult, isFetching: isLookingUpUsers }] = useLazyLookupAdminUsersQuery();
    const [triggerKycLookup, { data: kycLookupResult, isFetching: isLookingUpKyc }] = useLazyLookupAdminKycQuery();
    const [createApplication, { isLoading: isCreatingApplication }] = useCreateCorporateApplicationMutation();

    // Once a customer is picked, look for a draft they already have, so the admin
    // can continue it instead of creating a duplicate application.
    const { currentData: customerApplicationsResponse, isFetching: isCheckingDrafts } = useGetCorporateApplicationsQuery(
        { query: { userGroup, userId: selectedUser?.userId } },
        { skip: !selectedUser?.userId || Boolean(applicationId), refetchOnMountOrArgChange: true }
    );
    const existingDrafts = useMemo(() => {
        const raw = customerApplicationsResponse?.data ?? customerApplicationsResponse?.applications ?? customerApplicationsResponse ?? [];
        const list = Array.isArray(raw) ? raw : [];
        return list.filter((application) => (
            isDraftStatus(application) && applicationUserId(application) === selectedUser?.userId
        ));
    }, [customerApplicationsResponse, selectedUser]);

    const userResults = useMemo(() => {
        const data = userLookupResult?.data ?? userLookupResult ?? [];
        return Array.isArray(data) ? data : [];
    }, [userLookupResult]);

    const kycResults = useMemo(() => {
        const data = kycLookupResult?.data ?? kycLookupResult ?? [];
        return Array.isArray(data) ? data : [];
    }, [kycLookupResult]);

    const runLookup = () => {
        const value = lookupValue.trim();
        if (!value) return;
        triggerUserLookup({ query: { [lookupField]: value, userGroup } });
    };

    const browseCorporateKyc = () => {
        triggerKycLookup({ query: { userType: "CORPORATE", userGroup, limit: 50, offset: 0 } });
    };

    const handleCreate = async () => {
        if (!selectedUser?.userId) return;
        try {
            const result = await createApplication({
                userId: selectedUser.userId,
                body: {
                    accountName: accountName.trim() || undefined,
                    userGroup,
                },
            }).unwrap();
            const newApplicationId = result?.applicationId || result?.data?.applicationId;
            if (newApplicationId) {
                setApplicationId(newApplicationId);
                cogoToast.success("Application created — continue with the Company step.");
            }
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to create application");
        }
    };

    if (applicationId) {
        return (
            <div>
                <div className="alert alert-light-success" role="alert">
                    Application <strong>{applicationId}</strong> is ready for {selectedUser?.email || selectedUser?.userId || "the selected customer"}.
                    Use the steps on the left to continue — start with <strong>2. Company (CAC)</strong>.
                </div>
            </div>
        );
    }

    return (
        <div>
            <h5 className="mb-3">1. Find the customer</h5>
            <Row className="align-items-end mb-3">
                <Col md={3}>
                    <Form.Label>Lookup by</Form.Label>
                    <Form.Control as="select" value={lookupField} onChange={(e) => setLookupField(e.target.value)}>
                        <option value="email">Email</option>
                        <option value="userId">User ID</option>
                        <option value="username">Username</option>
                    </Form.Control>
                </Col>
                <Col md={6}>
                    <Form.Label>Value</Form.Label>
                    <Form.Control
                        type="text"
                        value={lookupValue}
                        onChange={(e) => setLookupValue(e.target.value)}
                        placeholder={lookupField === "email" ? "ops@acme.com" : "..."}
                        onKeyDown={(e) => e.key === "Enter" && runLookup()}
                    />
                </Col>
                <Col md={3}>
                    <Button variant="primary" block disabled={isLookingUpUsers || !lookupValue.trim()} onClick={runLookup}>
                        {isLookingUpUsers ? "Searching..." : "Search"}
                    </Button>
                </Col>
            </Row>

            {userResults.length > 0 && (
                <Table size="sm" bordered className="mb-4">
                    <thead>
                        <tr><th>Name</th><th>Email</th><th>Phone</th><th /></tr>
                    </thead>
                    <tbody>
                        {userResults.map((user) => (
                            <tr key={user.userId}>
                                <td>{[user.firstName, user.lastName].filter(Boolean).join(" ") || "-"}</td>
                                <td>{user.email || "-"}</td>
                                <td>{user.phone || "-"}</td>
                                <td>
                                    <Button size="sm" variant={selectedUser?.userId === user.userId ? "success" : "outline-primary"}
                                        onClick={() => setSelectedUser(user)}>
                                        {selectedUser?.userId === user.userId ? "Selected" : "Select"}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            {userResults.length === 0 && lookupValue && !isLookingUpUsers && (
                <div className="text-muted font-size-sm mb-4">No matches yet — run a search above.</div>
            )}

            {selectedUser && existingDrafts.length > 0 && (
                <div className="alert alert-light-warning mb-4" role="alert">
                    <div className="font-weight-bold mb-2">
                        {selectedUser.email || selectedUser.userId} already has
                        {existingDrafts.length === 1 ? " an unfinished application" : ` ${existingDrafts.length} unfinished applications`}.
                        Continue it instead of starting again?
                    </div>
                    {existingDrafts.map((draft) => (
                        <div key={draft.applicationId} className="d-flex align-items-center justify-content-between py-1">
                            <span>
                                {draft.accountName || draft.entityName || "Untitled"}{" "}
                                <code className="ml-1">{draft.applicationId}</code>
                            </span>
                            <Button size="sm" variant="primary" onClick={() => onResumeApplication(draft.applicationId)}>
                                Continue this draft
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {selectedUser && (
                <div className="mb-4 p-3 border rounded bg-light">
                    <h6 className="mb-3">
                        2. Create {existingDrafts.length > 0 ? "a new" : "the"} application for {selectedUser.email || selectedUser.userId}
                        {isCheckingDrafts && <span className="text-muted font-size-sm ml-2">checking for drafts…</span>}
                    </h6>
                    <Row className="align-items-end">
                        <Col md={6}>
                            <Form.Label>Account name (optional)</Form.Label>
                            <Form.Control
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                placeholder="Acme US Trading"
                            />
                        </Col>
                        <Col md={3}>
                            <Button variant="success" block disabled={isCreatingApplication} onClick={handleCreate}>
                                {isCreatingApplication ? "Creating..." : "Create Application"}
                            </Button>
                        </Col>
                    </Row>
                </div>
            )}

            <div className="mt-4">
                <Button variant="link" className="pl-0" onClick={() => setShowAdvanced((v) => !v)}>
                    {showAdvanced ? "Hide other options" : "Other options (resume a draft, browse existing KYC)"}
                </Button>
            </div>

            {showAdvanced && (
                <div className="mt-2 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Browse existing corporate KYC profiles</h6>
                        <Button size="sm" variant="outline-secondary" disabled={isLookingUpKyc} onClick={browseCorporateKyc}>
                            {isLookingUpKyc ? "Loading..." : "Browse Corporate KYC"}
                        </Button>
                    </div>
                    {kycResults.length > 0 && (
                        <Table size="sm" bordered className="mb-4">
                            <thead>
                                <tr><th>Entity Name</th><th>User Group</th><th>Status</th><th /></tr>
                            </thead>
                            <tbody>
                                {kycResults.map((kyc) => (
                                    <tr key={kyc.userId}>
                                        <td>{kyc?.legalEntityData?.entityName || "-"}</td>
                                        <td>{kyc.userGroup || "-"}</td>
                                        <td>{kyc.status || "-"}</td>
                                        <td>
                                            <Button size="sm" variant={selectedUser?.userId === kyc.userId ? "success" : "outline-primary"}
                                                onClick={() => setSelectedUser(kyc)}>
                                                {selectedUser?.userId === kyc.userId ? "Selected" : "Select"}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    <hr className="my-3" />
                    <h6>Continue an existing draft application by ID</h6>
                    <Row className="align-items-end">
                        <Col md={6}>
                            <Form.Control
                                type="text"
                                value={existingApplicationId}
                                onChange={(e) => setExistingApplicationId(e.target.value)}
                                placeholder="applicationId"
                            />
                        </Col>
                        <Col md={3}>
                            <Button
                                variant="outline-primary"
                                block
                                disabled={!existingApplicationId.trim()}
                                onClick={() => onResumeApplication(existingApplicationId.trim())}
                            >
                                Continue
                            </Button>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 2 — CAC verify + legal entity / company profile (B3 + B8)
 * ------------------------------------------------------------------ */

const emptyLegalEntityForm = () => ({
    entityName: "", companyRegistrationNumber: "", taxIdentificationNumber: "",
    countryOfIncorporation: "NGA", stateOfIncorporation: "", dateOfIncorporation: "",
    legalEntityType: "", legalEntityTypeOther: "", industry: "", industryOther: "",
    entityEmail: "", entityPhone: "", primarySignerTitle: "",
    fundingSource: [], fundingSourceOther: "", investmentObjective: "",
    legalAddress: { street_address: "", city: "", state: "", postal_code: "", country: "NGA" },
    financialProfile: {
        annualIncomeMin: "", annualIncomeMax: "", liquidNetWorthMin: "",
        liquidNetWorthMax: "", totalNetWorthMin: "", totalNetWorthMax: "",
    },
    disclosures: { isParticipatingFatca: false, isExemptFromTaxUnder501a: false },
});

const CompanyCacStep = ({ applicationId, parties }) => {
    const { data: applicationResponse, isFetching, refetch } = useGetCorporateApplicationQuery(
        { applicationId }, { skip: !applicationId }
    );
    const [verifyCac, { isLoading: isVerifyingCac }] = useVerifyCorporateCacMutation();
    const [manualVerifyCac, { isLoading: isManualVerifying }] = useManualVerifyCorporateCacMutation();
    const [updateApplication, { isLoading: isSaving }] = useUpdateCorporateApplicationMutation();
    const [triggerTin, { data: tinResult, isFetching: isLookingUpTin }] = useLazyGetCorporateApplicationTinQuery();
    const { currentData: eligibility } = useGetCorporateApplicationEligibilityQuery(
        { applicationId }, { skip: !applicationId }
    );
    const companyEditable = canEditCompany(applicationResponse);

    const [rc, setRc] = useState("");
    const [tin, setTin] = useState("");
    const [form, setForm] = useState(emptyLegalEntityForm());
    const [ubos, setUbos] = useState([]);

    const legalEntity = applicationResponse?.legalEntity || applicationResponse?.application || {};

    useEffect(() => {
        if (!legalEntity || Object.keys(legalEntity).length === 0) return;
        setForm((prev) => ({
            ...prev,
            entityName: legalEntity.entityName || prev.entityName,
            companyRegistrationNumber: legalEntity.companyRegistrationNumber || prev.companyRegistrationNumber,
            taxIdentificationNumber: legalEntity.taxIdentificationNumber || prev.taxIdentificationNumber,
            countryOfIncorporation: legalEntity.countryOfIncorporation || prev.countryOfIncorporation,
            stateOfIncorporation: legalEntity.stateOfIncorporation || prev.stateOfIncorporation,
            dateOfIncorporation: legalEntity.dateOfIncorporation || prev.dateOfIncorporation,
            legalEntityType: legalEntity.legalEntityType || prev.legalEntityType,
            legalEntityTypeOther: legalEntity.legalEntityTypeOther || prev.legalEntityTypeOther,
            industry: legalEntity.industry || prev.industry,
            industryOther: legalEntity.industryOther || prev.industryOther,
            primarySignerTitle: legalEntity.primarySignerTitle || prev.primarySignerTitle,
            fundingSourceOther: legalEntity.fundingSourceOther || prev.fundingSourceOther,
            entityEmail: legalEntity.entityEmail || prev.entityEmail,
            entityPhone: legalEntity.entityPhone || prev.entityPhone,
            investmentObjective: legalEntity.investmentObjective || prev.investmentObjective,
            fundingSource: legalEntity.fundingSource || prev.fundingSource,
            legalAddress: legalEntity.legalAddress ? {
                street_address: streetToText(legalEntity.legalAddress.street_address),
                city: legalEntity.legalAddress.city || "",
                state: legalEntity.legalAddress.state || "",
                postal_code: legalEntity.legalAddress.postal_code || "",
                country: legalEntity.legalAddress.country || "NGA",
            } : prev.legalAddress,
            financialProfile: legalEntity.financialProfile
                ? Object.fromEntries(Object.keys(prev.financialProfile).map((key) => [
                    key, legalEntity.financialProfile[key] ?? prev.financialProfile[key],
                ]))
                : prev.financialProfile,
            disclosures: legalEntity.disclosures || prev.disclosures,
        }));
        setUbos((legalEntity.ubos || []).map((u) => ({ ...u })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationResponse]);

    const handleVerifyCac = async () => {
        if (!companyEditable || !rc.trim()) return;
        try {
            const result = await verifyCac({ applicationId, body: { companyRegistrationNumber: rc.trim() } }).unwrap();
            cogoToast.success(`CAC verified: ${result?.entityName || "clear"}`);
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "CAC verify failed");
        }
    };

    const handleManualVerify = async () => {
        if (!companyEditable || (!rc.trim() && !form.companyRegistrationNumber)) return;
        try {
            await manualVerifyCac({
                applicationId,
                body: {
                    companyRegistrationNumber: rc.trim() || form.companyRegistrationNumber,
                    entityName: form.entityName || undefined,
                },
            }).unwrap();
            cogoToast.success("Manual CAC verify submitted");
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Manual CAC verify failed");
        }
    };

    const handleTinLookup = () => {
        if (!tin.trim()) return;
        triggerTin({ applicationId, tin: tin.trim() });
    };

    const toggleFundingSource = (value) => {
        setForm((prev) => ({
            ...prev,
            fundingSource: prev.fundingSource.includes(value)
                ? prev.fundingSource.filter((v) => v !== value)
                : [...prev.fundingSource, value],
        }));
    };

    // Updates the party's ownership row, adding it first if it isn't there yet.
    const updateUbo = (partyId, field, value) => {
        setUbos((prev) => (prev.some((u) => u.partyId === partyId)
            ? prev.map((u) => (u.partyId === partyId ? { ...u, [field]: value } : u))
            : [...prev, { partyId, percentageOwnership: 0, authorizedSigner: false, titleAtCompany: "", [field]: value }]));
    };

    const handleSave = async () => {
        if (!companyEditable) return;
        const body = {
            entityName: form.entityName || undefined,
            companyRegistrationNumber: form.companyRegistrationNumber || undefined,
            taxIdentificationNumber: form.taxIdentificationNumber || undefined,
            countryOfIncorporation: form.countryOfIncorporation || undefined,
            stateOfIncorporation: form.stateOfIncorporation || undefined,
            dateOfIncorporation: form.dateOfIncorporation || undefined,
            legalEntityType: form.legalEntityType || undefined,
            legalEntityTypeOther: form.legalEntityType === "other" ? form.legalEntityTypeOther : undefined,
            industry: form.industry || undefined,
            industryOther: form.industry === "other" ? form.industryOther : undefined,
            entityEmail: form.entityEmail || undefined,
            entityPhone: form.entityPhone || undefined,
            primarySignerTitle: form.primarySignerTitle || undefined,
            fundingSource: form.fundingSource.length ? form.fundingSource : undefined,
            fundingSourceOther: form.fundingSource.includes("other") ? form.fundingSourceOther : undefined,
            investmentObjective: form.investmentObjective || undefined,
            legalAddress: {
                street_address: form.legalAddress.street_address ? textToStreet(form.legalAddress.street_address) : undefined,
                city: form.legalAddress.city || undefined,
                state: form.legalAddress.state || undefined,
                postal_code: form.legalAddress.postal_code || undefined,
                country: form.legalAddress.country || undefined,
            },
            financialProfile: {
                annualIncomeMin: toNumberOrUndefined(form.financialProfile.annualIncomeMin),
                annualIncomeMax: toNumberOrUndefined(form.financialProfile.annualIncomeMax),
                liquidNetWorthMin: toNumberOrUndefined(form.financialProfile.liquidNetWorthMin),
                liquidNetWorthMax: toNumberOrUndefined(form.financialProfile.liquidNetWorthMax),
                totalNetWorthMin: toNumberOrUndefined(form.financialProfile.totalNetWorthMin),
                totalNetWorthMax: toNumberOrUndefined(form.financialProfile.totalNetWorthMax),
            },
            disclosures: form.disclosures,
            ubos: ubos.map((u) => ({
                partyId: u.partyId,
                percentageOwnership: Number(u.percentageOwnership) || 0,
                authorizedSigner: Boolean(u.authorizedSigner),
                titleAtCompany: u.titleAtCompany || undefined,
            })),
        };

        try {
            await updateApplication({ applicationId, body }).unwrap();
            cogoToast.success("Company details saved");
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to save company details");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;

    return (
        <div>
            <ProvisionErrorAlert eligibility={eligibility} />
            <EditLockAlert reason={companyLockReason(applicationResponse)} />
            <fieldset disabled={!companyEditable} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <h5 className="mb-3">CAC verification</h5>
            <Row className="align-items-end mb-4">
                <Col md={5}>
                    <Form.Label>Company registration number (RC / BN)</Form.Label>
                    <Form.Control type="text" value={rc} onChange={(e) => setRc(e.target.value)} placeholder="RC7011111" />
                </Col>
                <Col md={3}>
                    <Button variant="primary" block disabled={isVerifyingCac} onClick={handleVerifyCac}>
                        {isVerifyingCac ? "Verifying..." : "Verify (QoreID)"}
                    </Button>
                </Col>
                <Col md={4}>
                    <Button variant="outline-secondary" block disabled={isManualVerifying} onClick={handleManualVerify}>
                        {isManualVerifying ? "Submitting..." : "Manual Verify (lockout fallback)"}
                    </Button>
                </Col>
            </Row>

            <Row className="align-items-end mb-4">
                <Col md={5}>
                    <Form.Label>TIN lookup (optional)</Form.Label>
                    <Form.Control type="text" value={tin} onChange={(e) => setTin(e.target.value)} placeholder="12345678-0111" />
                </Col>
                <Col md={3}>
                    <Button variant="outline-primary" block disabled={isLookingUpTin} onClick={handleTinLookup}>
                        {isLookingUpTin ? "Looking up..." : "Lookup TIN"}
                    </Button>
                </Col>
                <Col md={4}>
                    {tinResult && <div className="text-muted font-size-sm">Result: {tinResult.result || (tinResult.verified ? "clear" : "-")}</div>}
                </Col>
            </Row>

            <hr />
            <h5 className="mb-3">Company profile {isFetching && <span className="text-muted font-size-sm">(loading...)</span>}</h5>
            <Row>
                <Col md={6} className="mb-3">
                    <Form.Label>Entity name</Form.Label>
                    <Form.Control value={form.entityName} onChange={(e) => setForm({ ...form, entityName: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Registration number</Form.Label>
                    <Form.Control value={form.companyRegistrationNumber} onChange={(e) => setForm({ ...form, companyRegistrationNumber: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Tax identification number (TIN)</Form.Label>
                    <Form.Control value={form.taxIdentificationNumber} onChange={(e) => setForm({ ...form, taxIdentificationNumber: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Country of incorporation</Form.Label>
                    <Form.Control value={form.countryOfIncorporation} onChange={(e) => setForm({ ...form, countryOfIncorporation: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>State of incorporation</Form.Label>
                    <Form.Control value={form.stateOfIncorporation} onChange={(e) => setForm({ ...form, stateOfIncorporation: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Date of incorporation</Form.Label>
                    <Form.Control type="date" value={form.dateOfIncorporation} onChange={(e) => setForm({ ...form, dateOfIncorporation: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Legal entity type</Form.Label>
                    <Form.Control as="select" value={form.legalEntityType} onChange={(e) => setForm({ ...form, legalEntityType: e.target.value })}>
                        <option value="">Select...</option>
                        {LEGAL_ENTITY_TYPES.map((v) => <option key={v} value={v}>{prettyEnum(v)}</option>)}
                    </Form.Control>
                    {form.legalEntityType === "other" && (
                        <Form.Control className="mt-2" placeholder="Describe entity type"
                            value={form.legalEntityTypeOther} onChange={(e) => setForm({ ...form, legalEntityTypeOther: e.target.value })} />
                    )}
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Industry</Form.Label>
                    <Form.Control as="select" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
                        <option value="">Select...</option>
                        {INDUSTRIES.map((v) => <option key={v} value={v}>{prettyEnum(v)}</option>)}
                    </Form.Control>
                    {form.industry === "other" && (
                        <Form.Control className="mt-2" placeholder="Describe industry"
                            value={form.industryOther} onChange={(e) => setForm({ ...form, industryOther: e.target.value })} />
                    )}
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Company email</Form.Label>
                    <Form.Control value={form.entityEmail} onChange={(e) => setForm({ ...form, entityEmail: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Company phone</Form.Label>
                    <Form.Control value={form.entityPhone} onChange={(e) => setForm({ ...form, entityPhone: e.target.value })} placeholder="+2348012345678" />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Primary signer title</Form.Label>
                    <Form.Control value={form.primarySignerTitle} onChange={(e) => setForm({ ...form, primarySignerTitle: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Label>Investment objective</Form.Label>
                    <Form.Control as="select" value={form.investmentObjective} onChange={(e) => setForm({ ...form, investmentObjective: e.target.value })}>
                        <option value="">Select...</option>
                        {INVESTMENT_OBJECTIVES.map((v) => <option key={v} value={v}>{prettyEnum(v)}</option>)}
                    </Form.Control>
                </Col>
            </Row>

            <Form.Label className="mt-2">Funding source (select one or more)</Form.Label>
            <div className="mb-3 d-flex flex-wrap">
                {COMPANY_FUNDING_SOURCES.map((v) => (
                    <Form.Check
                        key={v} inline type="checkbox" id={`funding-${v}`} label={prettyEnum(v)}
                        checked={form.fundingSource.includes(v)} onChange={() => toggleFundingSource(v)}
                    />
                ))}
            </div>
            {form.fundingSource.includes("other") && (
                <Form.Control className="mb-3" placeholder="Describe funding source"
                    value={form.fundingSourceOther} onChange={(e) => setForm({ ...form, fundingSourceOther: e.target.value })} />
            )}

            <h6 className="mt-3">Registered address</h6>
            <Row>
                <Col md={12} className="mb-3">
                    <Form.Label>Street address</Form.Label>
                    <Form.Control value={form.legalAddress.street_address}
                        onChange={(e) => setForm({ ...form, legalAddress: { ...form.legalAddress, street_address: e.target.value } })} />
                </Col>
                <Col md={4} className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control value={form.legalAddress.city}
                        onChange={(e) => setForm({ ...form, legalAddress: { ...form.legalAddress, city: e.target.value } })} />
                </Col>
                <Col md={4} className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Control value={form.legalAddress.state}
                        onChange={(e) => setForm({ ...form, legalAddress: { ...form.legalAddress, state: e.target.value } })} />
                </Col>
                <Col md={4} className="mb-3">
                    <Form.Label>Postal code</Form.Label>
                    <Form.Control value={form.legalAddress.postal_code}
                        onChange={(e) => setForm({ ...form, legalAddress: { ...form.legalAddress, postal_code: e.target.value } })} />
                </Col>
            </Row>

            <h6 className="mt-3">Financial profile (USD)</h6>
            <Row>
                {[
                    ["annualIncomeMin", "Annual income (min)"], ["annualIncomeMax", "Annual income (max)"],
                    ["liquidNetWorthMin", "Liquid net worth (min)"], ["liquidNetWorthMax", "Liquid net worth (max)"],
                    ["totalNetWorthMin", "Total net worth (min)"], ["totalNetWorthMax", "Total net worth (max)"],
                ].map(([key, label]) => (
                    <Col md={4} className="mb-3" key={key}>
                        <Form.Label>{label}</Form.Label>
                        <Form.Control type="number" value={form.financialProfile[key]}
                            onChange={(e) => setForm({ ...form, financialProfile: { ...form.financialProfile, [key]: e.target.value } })} />
                    </Col>
                ))}
            </Row>

            <h6 className="mt-3">Disclosures</h6>
            <Form.Check className="mb-2" type="checkbox" id="fatca" label="Participating FATCA institution"
                checked={form.disclosures.isParticipatingFatca}
                onChange={(e) => setForm({ ...form, disclosures: { ...form.disclosures, isParticipatingFatca: e.target.checked } })} />
            <Form.Check className="mb-3" type="checkbox" id="501a" label="Exempt from tax under 501(a)"
                checked={form.disclosures.isExemptFromTaxUnder501a}
                onChange={(e) => setForm({ ...form, disclosures: { ...form.disclosures, isExemptFromTaxUnder501a: e.target.checked } })} />

            <h6 className="mt-3">Ownership (UBOs)</h6>
            {parties.length === 0 && <div className="text-muted font-size-sm mb-3">Add parties first (Step 3) to assign ownership.</div>}
            {parties.length > 0 && (
                <Table size="sm" bordered className="mb-3">
                    <thead><tr><th>Party</th><th>% Ownership</th><th>Authorized Signer</th><th>Title</th></tr></thead>
                    <tbody>
                        {parties.map((party) => {
                            const ubo = ubos.find((u) => u.partyId === party.partyId) || { partyId: party.partyId };
                            return (
                                <tr key={party.partyId}>
                                    <td>{[party.givenName, party.familyName].filter(Boolean).join(" ") || party.partyId}</td>
                                    <td>
                                        <Form.Control size="sm" type="number" value={ubo.percentageOwnership ?? 0}
                                            onChange={(e) => updateUbo(party.partyId, "percentageOwnership", e.target.value)} />
                                    </td>
                                    <td className="text-center">
                                        <Form.Check type="checkbox" checked={Boolean(ubo.authorizedSigner)}
                                            onChange={(e) => updateUbo(party.partyId, "authorizedSigner", e.target.checked)} />
                                    </td>
                                    <td>
                                        <Form.Control size="sm" value={ubo.titleAtCompany || ""}
                                            onChange={(e) => updateUbo(party.partyId, "titleAtCompany", e.target.value)} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            <Button variant="success" disabled={isSaving || !companyEditable} onClick={handleSave}>
                {isSaving ? "Saving..." : "Save Company Details"}
            </Button>
            </fieldset>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 3 — Parties (B5)
 * ------------------------------------------------------------------ */

const emptyParty = () => ({
    givenName: "", familyName: "", email: "", phone: "", dateOfBirth: "",
    countryOfCitizenship: "NGA", countryOfBirth: "NGA", titleAtCompany: "",
    fundingSource: [],
    residentialAddress: { street_address: "", city: "", state: "", postal_code: "", country: "NGA" },
});

const PartiesStep = ({ applicationId, onPartiesChanged }) => {
    const { data: partiesResponse, isFetching, refetch } = useGetCorporateApplicationPartiesQuery(
        { applicationId }, { skip: !applicationId }
    );
    const [saveParties, { isLoading: isSavingParties }] = useSaveCorporateApplicationPartiesMutation();
    const [deleteParty, { isLoading: isDeletingParty }] = useDeleteCorporateApplicationPartyMutation();
    const { currentData: applicationResponse } = useGetCorporateApplicationQuery(
        { applicationId }, { skip: !applicationId }
    );
    const repsEditable = canEditReps(applicationResponse);

    const [draft, setDraft] = useState(emptyParty());
    const [editingPartyId, setEditingPartyId] = useState(null);

    const parties = useMemo(() => partiesResponse?.additionalParties || [], [partiesResponse]);

    useEffect(() => { onPartiesChanged?.(parties); }, [parties, onPartiesChanged]);

    const toggleFundingSource = (value) => {
        setDraft((prev) => ({
            ...prev,
            fundingSource: prev.fundingSource.includes(value)
                ? prev.fundingSource.filter((v) => v !== value)
                : [...prev.fundingSource, value],
        }));
    };

    const startEdit = (party) => {
        if (!repsEditable) return;
        setEditingPartyId(party.partyId);
        setDraft({
            ...emptyParty(),
            ...party,
            residentialAddress: party.residentialAddress ? {
                street_address: streetToText(party.residentialAddress.street_address),
                city: party.residentialAddress.city || "",
                state: party.residentialAddress.state || "",
                postal_code: party.residentialAddress.postal_code || "",
                country: party.residentialAddress.country || "NGA",
            } : emptyParty().residentialAddress,
        });
    };

    const resetDraft = () => { setDraft(emptyParty()); setEditingPartyId(null); };

    const handleSave = async () => {
        if (!repsEditable) return;
        const nextParty = {
            ...(editingPartyId ? { partyId: editingPartyId } : {}),
            givenName: draft.givenName, familyName: draft.familyName, email: draft.email, phone: draft.phone,
            dateOfBirth: draft.dateOfBirth, countryOfCitizenship: draft.countryOfCitizenship,
            countryOfBirth: draft.countryOfBirth, titleAtCompany: draft.titleAtCompany,
            fundingSource: draft.fundingSource,
            residentialAddress: {
                street_address: textToStreet(draft.residentialAddress.street_address),
                city: draft.residentialAddress.city,
                state: draft.residentialAddress.state,
                postal_code: draft.residentialAddress.postal_code,
                country: draft.residentialAddress.country,
            },
        };

        const nextList = editingPartyId
            // Keep whatever else the server holds for this party (ids, uploads, tax forms);
            // the list is saved whole, so dropping them here would wipe them.
            ? parties.map((p) => (p.partyId === editingPartyId ? { ...p, ...nextParty } : p))
            : [...parties, nextParty];

        try {
            await saveParties({ applicationId, additionalParties: nextList }).unwrap();
            cogoToast.success(editingPartyId ? "Party updated" : "Party added");
            resetDraft();
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to save party");
        }
    };

    const handleDelete = async (partyId) => {
        if (!repsEditable) return;
        try {
            await deleteParty({ applicationId, partyId }).unwrap();
            cogoToast.success("Party removed");
            if (editingPartyId === partyId) resetDraft();
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to remove party");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;

    return (
        <div>
            <EditLockAlert reason={repsLockReason(applicationResponse)} />
            <h5 className="mb-3">Company representatives {isFetching && <span className="text-muted font-size-sm">(loading...)</span>}</h5>
            <Table size="sm" bordered className="mb-4">
                <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Title</th><th /></tr></thead>
                <tbody>
                    {parties.map((party) => (
                        <tr key={party.partyId}>
                            <td>{[party.givenName, party.familyName].filter(Boolean).join(" ") || "-"}</td>
                            <td>{party.email || "-"}</td>
                            <td>{party.phone || "-"}</td>
                            <td>{party.titleAtCompany || "-"}</td>
                            <td className="text-right">
                                <Button size="sm" variant="outline-primary" className="mr-2" disabled={!repsEditable} onClick={() => startEdit(party)}>Edit</Button>
                                <Button size="sm" variant="outline-danger" disabled={!repsEditable || isDeletingParty} onClick={() => handleDelete(party.partyId)}>Remove</Button>
                            </td>
                        </tr>
                    ))}
                    {parties.length === 0 && (
                        <tr><td colSpan="5" className="text-center text-muted py-5">No representatives added yet.</td></tr>
                    )}
                </tbody>
            </Table>

            <fieldset disabled={!repsEditable} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <h6>{editingPartyId ? "Edit representative" : "Add a representative"}</h6>
            <Row>
                <Col md={6} className="mb-2"><Form.Label>First name</Form.Label>
                    <Form.Control value={draft.givenName} onChange={(e) => setDraft({ ...draft, givenName: e.target.value })} /></Col>
                <Col md={6} className="mb-2"><Form.Label>Last name</Form.Label>
                    <Form.Control value={draft.familyName} onChange={(e) => setDraft({ ...draft, familyName: e.target.value })} /></Col>
                <Col md={6} className="mb-2"><Form.Label>Email</Form.Label>
                    <Form.Control value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Col>
                <Col md={6} className="mb-2"><Form.Label>Phone</Form.Label>
                    <Form.Control value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="+2348011112222" /></Col>
                <Col md={6} className="mb-2"><Form.Label>Date of birth</Form.Label>
                    <Form.Control type="date" value={draft.dateOfBirth} onChange={(e) => setDraft({ ...draft, dateOfBirth: e.target.value })} /></Col>
                <Col md={6} className="mb-2"><Form.Label>Title at company</Form.Label>
                    <Form.Control value={draft.titleAtCompany} onChange={(e) => setDraft({ ...draft, titleAtCompany: e.target.value })} placeholder="Director" /></Col>
                <Col md={6} className="mb-2"><Form.Label>Country of citizenship</Form.Label>
                    <Form.Control value={draft.countryOfCitizenship} onChange={(e) => setDraft({ ...draft, countryOfCitizenship: e.target.value })} /></Col>
                <Col md={6} className="mb-2"><Form.Label>Country of birth</Form.Label>
                    <Form.Control value={draft.countryOfBirth} onChange={(e) => setDraft({ ...draft, countryOfBirth: e.target.value })} /></Col>
            </Row>

            <Form.Label className="mt-1">Funding source</Form.Label>
            <div className="mb-3 d-flex flex-wrap">
                {PARTY_FUNDING_SOURCES.map((v) => (
                    <Form.Check key={v} inline type="checkbox" id={`party-funding-${v}`} label={prettyEnum(v)}
                        checked={draft.fundingSource.includes(v)} onChange={() => toggleFundingSource(v)} />
                ))}
            </div>

            <h6 className="mt-2">Residential address</h6>
            <Row>
                <Col md={12} className="mb-2"><Form.Label>Street address</Form.Label>
                    <Form.Control value={draft.residentialAddress.street_address}
                        onChange={(e) => setDraft({ ...draft, residentialAddress: { ...draft.residentialAddress, street_address: e.target.value } })} /></Col>
                <Col md={4} className="mb-2"><Form.Label>City</Form.Label>
                    <Form.Control value={draft.residentialAddress.city}
                        onChange={(e) => setDraft({ ...draft, residentialAddress: { ...draft.residentialAddress, city: e.target.value } })} /></Col>
                <Col md={4} className="mb-2"><Form.Label>State</Form.Label>
                    <Form.Control value={draft.residentialAddress.state}
                        onChange={(e) => setDraft({ ...draft, residentialAddress: { ...draft.residentialAddress, state: e.target.value } })} /></Col>
                <Col md={4} className="mb-2"><Form.Label>Postal code</Form.Label>
                    <Form.Control value={draft.residentialAddress.postal_code}
                        onChange={(e) => setDraft({ ...draft, residentialAddress: { ...draft.residentialAddress, postal_code: e.target.value } })} /></Col>
            </Row>

            <div className="mt-3">
                <Button variant="success" className="mr-2" disabled={isSavingParties} onClick={handleSave}>
                    {isSavingParties ? "Saving..." : editingPartyId ? "Update Representative" : "Add Representative"}
                </Button>
                {editingPartyId && <Button variant="light" onClick={resetDraft}>Cancel</Button>}
            </div>
            </fieldset>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 4 — Party documents (B6)
 * ------------------------------------------------------------------ */

const PartyDocumentUploader = ({ applicationId, userId, party, docType, label, existingDocuments }) => {
    const [uploadDocument, { isLoading }] = useUploadCorporateApplicationDocumentMutation();
    const [idType, setIdType] = useState("NIN");
    // IDs such as the NIN slip or a driver's licence have two sides; the wizard
    // used to send every ID as "front", so the back could never be uploaded.
    const [side, setSide] = useState("front");
    const [file, setFile] = useState(null);
    const isGovernmentId = docType === "GOVERNMENT_ID";
    const existing = latestDocument(existingDocuments, docType, isGovernmentId ? side : undefined);

    const handleUpload = async () => {
        if (!file) return;
        try {
            const document = await readFileAsDataUrl(file);
            const body = { userId, type: docType, refId: party.partyId, document };
            if (isGovernmentId) { body.idType = idType; body.side = side; }
            await uploadDocument({ applicationId, body }).unwrap();
            cogoToast.success(`${label}${isGovernmentId ? ` (${side})` : ""} uploaded for ${party.givenName || party.partyId}`);
            setFile(null);
        } catch (error) {
            cogoToast.error(error?.data?.error || `Failed to upload ${label}`);
        }
    };

    return (
        <div className="py-2 border-bottom">
            <Row className="align-items-center">
                <Col md={3}>
                    <div className="font-weight-bold">{label}</div>
                    <UploadedStatus document={existing} />
                </Col>
                {isGovernmentId && (
                    <Col md={3}>
                        <Row className="no-gutters">
                            <Col xs={7} className="pr-1">
                                <Form.Control as="select" size="sm" value={idType} onChange={(e) => setIdType(e.target.value)} aria-label="ID type">
                                    <option value="NIN">NIN</option>
                                    <option value="PASSPORT">Passport</option>
                                    <option value="DRIVER_LICENSE">Driver's licence</option>
                                    <option value="OTHER_GOV_ID">Other</option>
                                </Form.Control>
                            </Col>
                            <Col xs={5}>
                                <Form.Control as="select" size="sm" value={side} onChange={(e) => setSide(e.target.value)} aria-label="Side">
                                    <option value="front">Front</option>
                                    <option value="back">Back</option>
                                </Form.Control>
                            </Col>
                        </Row>
                    </Col>
                )}
                <Col md={isGovernmentId ? 4 : 7}>
                    <FilePicker id={`party-doc-${party.partyId}-${docType}`} file={file} onChange={setFile} />
                </Col>
                <Col md={2}>
                    <Button size="sm" variant="primary" block disabled={!file || isLoading} onClick={handleUpload}>
                        {isLoading ? "Uploading..." : existing ? "Replace" : "Upload"}
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

// Documents already uploaded for this application, from the saved application:
// parties[].documents for representatives, orgDocuments for the company.
const useApplicationDocuments = (applicationId) => {
    const { currentData: applicationResponse } = useGetCorporateApplicationQuery(
        { applicationId }, { skip: !applicationId }
    );
    return useMemo(() => {
        const byParty = {};
        (applicationResponse?.parties || []).forEach((party) => {
            if (party?.partyId) byParty[party.partyId] = party.documents || [];
        });
        return { byParty, org: applicationResponse?.orgDocuments || [] };
    }, [applicationResponse]);
};

const PartyDocumentsStep = ({ applicationId, userId, parties }) => {
    const documents = useApplicationDocuments(applicationId);

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;
    if (!userId) return <EmptyState>Select the corporate user in Step 1 before uploading documents.</EmptyState>;
    if (parties.length === 0) return <EmptyState>Add company representatives first (Step 3).</EmptyState>;

    return (
        <div>
            <h5 className="mb-3">Party documents</h5>
            <div className="text-muted font-size-sm mb-4">
                Upload a government ID (both sides if it has two) and a selfie for each representative.
            </div>
            {parties.map((party) => (
                <div key={party.partyId} className="mb-4 p-3 border rounded">
                    <div className="font-weight-bolder mb-2">
                        {[party.givenName, party.familyName].filter(Boolean).join(" ") || party.partyId}
                    </div>
                    <PartyDocumentUploader applicationId={applicationId} userId={userId} party={party}
                        docType="GOVERNMENT_ID" label="Government ID" existingDocuments={documents.byParty[party.partyId]} />
                    <PartyDocumentUploader applicationId={applicationId} userId={userId} party={party}
                        docType="PASSPORT" label="Selfie" existingDocuments={documents.byParty[party.partyId]} />
                </div>
            ))}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 5 — Org documents (B9)
 * ------------------------------------------------------------------ */

const ORG_DOCUMENT_TYPES = [
    { type: "CAC_REGISTRATION", label: "CAC Registration" },
    { type: "ENTITY_GOVERNING_DOCUMENT", label: "Entity Governing Document (MEMART)" },
    { type: "BOARD_RESOLUTION", label: "Board Resolution" },
];

const OrgDocumentsStep = ({ applicationId, userId }) => {
    const [uploadDocument, { isLoading }] = useUploadCorporateApplicationDocumentMutation();
    const [files, setFiles] = useState({});
    const [uploadingType, setUploadingType] = useState(null);
    const documents = useApplicationDocuments(applicationId);

    const handleUpload = async (type, label) => {
        const file = files[type];
        if (!file) return;
        setUploadingType(type);
        try {
            const document = await readFileAsDataUrl(file);
            await uploadDocument({ applicationId, body: { userId, type, document } }).unwrap();
            cogoToast.success(`${label} uploaded`);
            setFiles((prev) => ({ ...prev, [type]: null }));
        } catch (error) {
            cogoToast.error(error?.data?.error || `Failed to upload ${label}`);
        } finally {
            setUploadingType(null);
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;
    if (!userId) return <EmptyState>Select the corporate user in Step 1 before uploading documents.</EmptyState>;

    return (
        <div>
            <h5 className="mb-3">Company (org) documents</h5>
            {ORG_DOCUMENT_TYPES.map(({ type, label }) => {
                const existing = latestDocument(documents.org, type);
                return (
                    <Row className="align-items-center py-2 border-bottom" key={type}>
                        <Col md={3}>
                            <div className="font-weight-bold">{label}</div>
                            <UploadedStatus document={existing} />
                        </Col>
                        <Col md={6}>
                            <FilePicker
                                id={`org-doc-${type}`}
                                file={files[type]}
                                onChange={(file) => setFiles((prev) => ({ ...prev, [type]: file }))}
                            />
                        </Col>
                        <Col md={3}>
                            <Button size="sm" variant="primary" block disabled={!files[type] || isLoading} onClick={() => handleUpload(type, label)}>
                                {uploadingType === type ? "Uploading..." : existing ? "Replace" : "Upload"}
                            </Button>
                        </Col>
                    </Row>
                );
            })}
        </div>
    );
};

const AgreementsStep = ({ applicationId }) => {
    const { data: agreementsResponse, isFetching, refetch } = useGetCorporateApplicationAgreementsQuery(
        { applicationId }, { skip: !applicationId }
    );
    const [acceptAgreements, { isLoading }] = useAcceptCorporateApplicationAgreementsMutation();

    const agreements = agreementsResponse?.agreements || [];
    const accepted = Boolean(agreementsResponse?.agreementsAcceptedAt);

    const handleAccept = async () => {
        try {
            await acceptAgreements({ applicationId, body: { version: agreementsResponse?.version } }).unwrap();
            cogoToast.success("Agreements accepted");
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to accept agreements");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;

    return (
        <div>
            <h5 className="mb-3">Agreements {isFetching && <span className="text-muted font-size-sm">(loading...)</span>}</h5>
            <ul>
                {agreements.map((agreement) => (
                    <li key={agreement.id}>
                        <a href={agreement.url} target="_blank" rel="noreferrer">{agreement.title}</a>
                    </li>
                ))}
            </ul>
            {accepted ? (
                <div className="alert alert-light-success">
                    Accepted on {agreementsResponse?.agreementsAcceptedAt} (version {agreementsResponse?.version})
                </div>
            ) : (
                <Button variant="success" disabled={isLoading || agreements.length === 0} onClick={handleAccept}>
                    {isLoading ? "Submitting..." : "Accept Agreements"}
                </Button>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 7 — Review & handoff (eligibility)
 * ------------------------------------------------------------------ */

const ReviewStep = ({ applicationId }) => {
    const { data: eligibility, isFetching, refetch } = useGetCorporateApplicationEligibilityQuery(
        { applicationId }, { skip: !applicationId, pollingInterval: 0 }
    );
    const [syncProvision, { isLoading: isSyncing }] = useSyncCorporateApplicationProvisionMutation();

    const handleSync = async () => {
        try {
            await syncProvision({ applicationId }).unwrap();
            cogoToast.success("Verifications submitted");
            refetch();
        } catch (error) {
            cogoToast.error(error?.data?.error || "Sync failed");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;

    return (
        <div>
            <h5 className="mb-3">Eligibility {isFetching && <span className="text-muted font-size-sm">(refreshing...)</span>}</h5>
            <div className="mb-3">
                <Badge variant={eligibility?.eligible ? "success" : "warning"} className="mr-2">
                    {eligibility?.eligible ? "Eligible" : "Not yet eligible"}
                </Badge>
                <Badge variant={eligibility?.canCreateAccount ? "success" : "secondary"}>
                    {eligibility?.canCreateAccount ? "Can create account" : "Cannot create account yet"}
                </Badge>
            </div>
            <ProvisionErrorAlert eligibility={eligibility} className="mb-3" />
            {eligibility?.blockedReason && !getAlpacaProvisionError(eligibility) && (
                <div className="alert alert-light-warning">{eligibility.blockedReason}</div>
            )}
            {Array.isArray(eligibility?.missing) && eligibility.missing.length > 0 && (
                <>
                    <div className="font-weight-bold mb-2">Still missing:</div>
                    <ul>
                        {eligibility.missing.map((item) => <li key={item}><code>{item}</code></li>)}
                    </ul>
                </>
            )}

            <div className="alert alert-light-info mt-3">
                Once org and party documents are uploaded, this application moves to <strong>Part A review</strong> — use the
                Corporate Accounts queue (this same page's other tabs) to approve documents, approve the application, and
                submit broker verifications. Come back here or to the eligibility panel to confirm before moving to W-8BEN-E.
            </div>

            <Button variant="outline-primary" disabled={isSyncing} onClick={handleSync}>
                {isSyncing ? "Retrying..." : "Retry Verifications / Provision Sync"}
            </Button>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 8 — W-8BEN-E wizard (B11, server-driven)
 * ------------------------------------------------------------------ */

const W8FieldControl = ({ field, value, onChange }) => {
    if (field.type === "boolean") {
        return (
            <Form.Check type="checkbox" id={`w8-${field.id}`} label={field.label} checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)} />
        );
    }
    if (field.type === "select") {
        return (
            <>
                <Form.Label>{field.label}</Form.Label>
                <Form.Control as="select" value={value || ""} onChange={(e) => onChange(e.target.value)}>
                    <option value="">Select...</option>
                    {(field.options || []).map((option) => (
                        <option key={option.value} value={option.value}>{option.label || prettyEnum(option.value)}</option>
                    ))}
                </Form.Control>
            </>
        );
    }
    if (field.type === "address") {
        const address = value || {};
        const set = (key, v) => onChange({ ...address, [key]: v });
        return (
            <>
                <Form.Label>{field.label}</Form.Label>
                <Row>
                    <Col md={12} className="mb-2">
                        <Form.Control placeholder="Street address"
                            value={address.street_address !== undefined ? streetToText(address.street_address) : (address.street1 ?? "")}
                            onChange={(e) => set("street_address", e.target.value ? [e.target.value] : [])} />
                    </Col>
                    <Col md={4} className="mb-2"><Form.Control placeholder="City" value={address.city || ""} onChange={(e) => set("city", e.target.value)} /></Col>
                    <Col md={4} className="mb-2"><Form.Control placeholder="State" value={address.state || ""} onChange={(e) => set("state", e.target.value)} /></Col>
                    <Col md={4} className="mb-2"><Form.Control placeholder="Postal code" value={address.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} /></Col>
                    <Col md={12}><Form.Control placeholder="Country" value={address.country || ""} onChange={(e) => set("country", e.target.value)} /></Col>
                </Row>
            </>
        );
    }
    // text (default)
    return (
        <>
            <Form.Label>{field.label}</Form.Label>
            <Form.Control value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </>
    );
};

const W8beneStep = ({ applicationId, parties }) => {
    const { data: resumeData, isFetching, refetch } = useGetCorporateW8beneQuery(
        { applicationId }, { skip: !applicationId }
    );
    const [startW8, { isLoading: isStarting }] = useStartCorporateW8beneMutation();
    const [saveAnswers, { isLoading: isSavingAnswers }] = useSaveCorporateW8beneAnswersMutation();
    const [previewW8, { isLoading: isPreviewing }] = usePreviewCorporateW8beneMutation();
    const [signW8, { isLoading: isSigning }] = useSignCorporateW8beneMutation();
    const [submitW8, { isLoading: isSubmitting }] = useSubmitCorporateW8beneMutation();

    const [wizardState, setWizardState] = useState(null);
    const [answers, setAnswers] = useState({});
    const [signature, setSignature] = useState({ fullName: "", capacity: "", authorised: true, signerPartyId: "" });

    useEffect(() => {
        if (resumeData) setWizardState(resumeData);
    }, [resumeData]);

    useEffect(() => {
        if (wizardState?.ui?.fields) {
            const seeded = {};
            wizardState.ui.fields.forEach((field) => {
                seeded[field.id] = field.value ?? wizardState?.answers?.[field.id] ?? (field.type === "boolean" ? false : field.type === "address" ? {} : "");
            });
            setAnswers(seeded);
        }
    }, [wizardState]);

    const handleStart = async () => {
        try {
            const result = await startW8({ applicationId }).unwrap();
            setWizardState(result);
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to start W-8BEN-E");
        }
    };

    const handleContinue = async () => {
        try {
            const result = await saveAnswers({ applicationId, step: wizardState.currentStep, answers }).unwrap();
            setWizardState(result);
        } catch (error) {
            cogoToast.error(error?.data?.manualReview?.message || error?.data?.error || "Failed to save answers");
            refetch();
        }
    };

    const handlePreview = async () => {
        try {
            const result = await previewW8({ applicationId }).unwrap();
            setWizardState((prev) => ({ ...prev, ...result }));
        } catch (error) {
            cogoToast.error(error?.data?.error || "Preview failed");
        }
    };

    const handleSign = async () => {
        try {
            const result = await signW8({ applicationId, body: signature }).unwrap();
            setWizardState((prev) => ({ ...prev, ...result }));
            cogoToast.success("Signed — now submit");
        } catch (error) {
            cogoToast.error(error?.data?.error || "Sign failed");
        }
    };

    const handleSubmit = async () => {
        try {
            const result = await submitW8({ applicationId }).unwrap();
            setWizardState((prev) => ({ ...prev, ...result }));
            cogoToast.success("W-8BEN-E submitted");
        } catch (error) {
            cogoToast.error(error?.data?.error || "Submit failed");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;
    if (isFetching && !wizardState) return <EmptyState>Loading W-8BEN-E status...</EmptyState>;

    const status = wizardState?.status;
    const currentStep = wizardState?.currentStep;

    if (wizardState?.started === false) {
        return (
            <div>
                <h5 className="mb-3">W-8BEN-E certification</h5>
                <p className="text-muted">
                    Only available once verifications are clear (see the Review step). Starting seeds the form from the
                    company profile you saved in Step 2.
                </p>
                <Button variant="primary" disabled={isStarting} onClick={handleStart}>
                    {isStarting ? "Starting..." : "Start W-8BEN-E"}
                </Button>
            </div>
        );
    }

    if (status === "MANUAL_REVIEW") {
        return (
            <div>
                <h5 className="mb-3">W-8BEN-E — under compliance review</h5>
                <div className="alert alert-light-warning">
                    {wizardState?.manualReview?.message || "This certification needs compliance review."}
                </div>
                {(wizardState?.manualReview?.reasons || wizardState?.derived?.manualReviewReasons || []).map((reason) => (
                    <div key={reason}><code>{reason}</code></div>
                ))}
                <div className="text-muted font-size-sm mt-2">
                    Resolve from the Part A review queue (Approve / Reject W-8BEN-E), then refresh this step.
                </div>
                <Button variant="outline-primary" className="mt-3" onClick={refetch}>Refresh</Button>
            </div>
        );
    }

    if (["SUBMITTED", "APPROVED", "PENDING_REVIEW"].includes(status)) {
        return (
            <div>
                <h5 className="mb-3">W-8BEN-E — submitted</h5>
                <div className="alert alert-light-success">
                    {wizardState?.ui?.description || "The W-8BEN-E certification has been submitted."}
                </div>
                {wizardState?.document?.pdfBase64 && (
                    <Button variant="outline-primary" onClick={() => openBase64Pdf(wizardState.document.pdfBase64)}>
                        View signed PDF
                    </Button>
                )}
            </div>
        );
    }

    if (currentStep === "SIGN" || status === "READY_TO_SIGN") {
        return (
            <div>
                <h5 className="mb-3">W-8BEN-E — sign</h5>
                {wizardState?.document?.previewPdfBase64 && (
                    <Button variant="outline-secondary" className="mb-3" onClick={() => openBase64Pdf(wizardState.document.previewPdfBase64)}>
                        View draft PDF
                    </Button>
                )}
                <Row>
                    <Col md={6} className="mb-2"><Form.Label>Full name</Form.Label>
                        <Form.Control value={signature.fullName} onChange={(e) => setSignature({ ...signature, fullName: e.target.value })} /></Col>
                    <Col md={6} className="mb-2"><Form.Label>Capacity</Form.Label>
                        <Form.Control value={signature.capacity} onChange={(e) => setSignature({ ...signature, capacity: e.target.value })} placeholder="Director" /></Col>
                    <Col md={6} className="mb-2"><Form.Label>Signer (party)</Form.Label>
                        <Form.Control as="select" value={signature.signerPartyId} onChange={(e) => setSignature({ ...signature, signerPartyId: e.target.value })}>
                            <option value="">Select...</option>
                            {parties.map((party) => (
                                <option key={party.partyId} value={party.partyId}>
                                    {[party.givenName, party.familyName].filter(Boolean).join(" ") || party.partyId}
                                </option>
                            ))}
                        </Form.Control>
                    </Col>
                    <Col md={6} className="mb-2 d-flex align-items-end">
                        <Form.Check type="checkbox" id="authorised" label="Authorised to sign"
                            checked={signature.authorised} onChange={(e) => setSignature({ ...signature, authorised: e.target.checked })} />
                    </Col>
                </Row>
                <Button variant="primary" className="mr-2" disabled={isSigning} onClick={handleSign}>
                    {isSigning ? "Signing..." : "Sign"}
                </Button>
                <Button variant="success" disabled={isSubmitting} onClick={handleSubmit}>
                    {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
            </div>
        );
    }

    if (currentStep === "REVIEW") {
        return (
            <div>
                <h5 className="mb-3">W-8BEN-E — review</h5>
                <p className="text-muted">All questionnaire steps are complete. Preview the draft certification before signing.</p>
                <Button variant="primary" disabled={isPreviewing} onClick={handlePreview}>
                    {isPreviewing ? "Generating..." : "Preview"}
                </Button>
            </div>
        );
    }

    // Mid-questionnaire step: render server-driven fields
    return (
        <div>
            <h5 className="mb-1">{wizardState?.ui?.title || "W-8BEN-E"}</h5>
            {wizardState?.ui?.description && <p className="text-muted">{wizardState.ui.description}</p>}
            {typeof wizardState?.progress?.percent === "number" && (
                <div className="mb-3 text-muted font-size-sm">Progress: {wizardState.progress.percent}%</div>
            )}
            <Row>
                {(wizardState?.ui?.fields || []).map((field) => (
                    <Col md={field.type === "address" ? 12 : 6} className="mb-3" key={field.id}>
                        <W8FieldControl
                            field={field}
                            value={answers[field.id]}
                            onChange={(value) => setAnswers((prev) => ({ ...prev, [field.id]: value }))}
                        />
                    </Col>
                ))}
            </Row>
            <Button variant="primary" disabled={isSavingAnswers} onClick={handleContinue}>
                {isSavingAnswers ? "Saving..." : "Continue"}
            </Button>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Step 9 — Create account (B13)
 * ------------------------------------------------------------------ */

const CreateAccountStep = ({ applicationId, onCreated }) => {
    const { data: eligibility, isFetching } = useGetCorporateApplicationEligibilityQuery(
        { applicationId }, { skip: !applicationId }
    );
    const [createAccount, { isLoading, data: createdAccount }] = useCreateCorporateAccountMutation();

    const handleCreate = async () => {
        try {
            const result = await createAccount({ applicationId }).unwrap();
            cogoToast.success("Trading account created");
            onCreated?.(result);
        } catch (error) {
            cogoToast.error(error?.data?.error || "Failed to create account");
        }
    };

    if (!applicationId) return <EmptyState>Create or select an application first (Step 1).</EmptyState>;

    return (
        <div>
            <h5 className="mb-3">Create trading account {isFetching && <span className="text-muted font-size-sm">(checking eligibility...)</span>}</h5>
            <ProvisionErrorAlert eligibility={eligibility} className="mb-3" />
            {!eligibility?.canCreateAccount && !getAlpacaProvisionError(eligibility) && (
                <div className="alert alert-light-warning">
                    {eligibility?.blockedReason || "Not yet eligible to create the account."}
                </div>
            )}
            <Button variant="success" disabled={!eligibility?.canCreateAccount || isLoading} onClick={handleCreate}>
                {isLoading ? "Creating..." : "Create Account"}
            </Button>
            {createdAccount && (
                <pre className="bg-light p-3 mt-3 rounded" style={{ fontSize: 12 }}>
                    {JSON.stringify(createdAccount, null, 2)}
                </pre>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 *  Wizard shell
 * ------------------------------------------------------------------ */

const STEP_LABEL = Object.fromEntries(WIZARD_STEPS.map(({ key, label }) => [key, label]));

const CreateCorporateApplicationWizard = ({ show, onHide, userGroup, resumeApplicationId }) => {
    const [step, setStep] = useState("find");
    const [selectedUser, setSelectedUser] = useState(null);
    const [applicationId, setApplicationId] = useState(null);
    const [parties, setParties] = useState([]);
    // True while reopening a saved draft: once its data has loaded, jump to the
    // first step that isn't finished yet.
    const [jumpToNextStep, setJumpToNextStep] = useState(false);

    useEffect(() => {
        if (!show) {
            setStep("find");
            setSelectedUser(null);
            setApplicationId(null);
            setParties([]);
            setJumpToNextStep(false);
        } else if (resumeApplicationId) {
            setApplicationId(resumeApplicationId);
            setJumpToNextStep(true);
        }
    }, [show, resumeApplicationId]);

    const resumeApplication = (id) => {
        if (!id) return;
        setApplicationId(id);
        setJumpToNextStep(true);
    };

    // Saved progress for the open application. Every step already reloads its own
    // data from these endpoints; the shell reads them too so a resumed draft knows
    // its customer, its parties and which steps are done.
    const { currentData: applicationResponse, isFetching: isFetchingApplication, isError: applicationFailed } = useGetCorporateApplicationQuery(
        { applicationId }, { skip: !applicationId }
    );
    const { currentData: partiesResponse, isFetching: isFetchingParties } = useGetCorporateApplicationPartiesQuery(
        { applicationId }, { skip: !applicationId }
    );
    const { currentData: agreementsResponse, isFetching: isFetchingAgreements } = useGetCorporateApplicationAgreementsQuery(
        { applicationId }, { skip: !applicationId }
    );

    // The document steps need the customer's userId; a resumed draft brings it
    // from the saved application instead of the Step 1 search.
    useEffect(() => {
        if (!applicationResponse || selectedUser?.userId) return;
        const savedUser = applicationResponse?.user || {};
        const userId = savedUser.userId || applicationResponse?.application?.userId || applicationResponse?.userId;
        if (userId) setSelectedUser({ ...savedUser, userId });
    }, [applicationResponse, selectedUser]);

    useEffect(() => {
        if (partiesResponse) setParties(partiesResponse?.additionalParties || []);
    }, [partiesResponse]);

    const progress = useMemo(() => {
        const saved = applicationResponse?.application || {};
        const legalEntity = applicationResponse?.legalEntity || saved;
        const qoreidKyb = applicationResponse?.qoreidKyb || {};
        return {
            company: Boolean(
                legalEntity?.entityName || saved?.cacVerified || qoreidKyb?.cacVerified || qoreidKyb?.matches?.cacCheckVerified
            ),
            parties: (partiesResponse?.additionalParties || []).length > 0,
            agreements: Boolean(agreementsResponse?.agreementsAcceptedAt || saved?.agreementsAcceptedAt),
        };
    }, [applicationResponse, partiesResponse, agreementsResponse]);

    useEffect(() => {
        if (!jumpToNextStep || !applicationId) return;
        if (applicationFailed && !isFetchingApplication) {
            setJumpToNextStep(false);
            setApplicationId(null);
            setStep("find");
            cogoToast.error("Couldn't load that application. Check the ID and try again.");
            return;
        }
        if (isFetchingApplication || isFetchingParties || isFetchingAgreements) return;
        if (!applicationResponse) return;
        // Documents have no "done" flag here, so after Parties the next stop is
        // Party Documents until agreements are accepted; the admin can skip ahead.
        const next = !progress.company ? "company"
            : !progress.parties ? "parties"
                : !progress.agreements ? "party-docs"
                    : "review";
        setStep(next);
        setJumpToNextStep(false);
        cogoToast.info(`Continuing application from ${STEP_LABEL[next]}`);
    }, [jumpToNextStep, applicationId, applicationResponse, applicationFailed, isFetchingApplication,
        isFetchingParties, isFetchingAgreements, progress]);

    const doneSteps = {
        find: Boolean(applicationId),
        company: progress.company,
        parties: progress.parties,
        agreements: progress.agreements,
    };

    return (
        <Modal show={show} onHide={onHide} centered size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{resumeApplicationId ? "Continue Corporate Application" : "Create Corporate Application"}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: "70vh" }}>
                <Row style={{ minHeight: "60vh" }}>
                    <Col lg={3} className="border-right mb-5 mb-lg-0">
                        <Nav className="flex-column">
                            {WIZARD_STEPS.map(({ key, label }) => {
                                const locked = key !== "find" && !applicationId;
                                return (
                                    <Nav.Item key={key}>
                                        <Nav.Link
                                            active={step === key}
                                            disabled={locked}
                                            onClick={() => {
                                                if (locked) {
                                                    cogoToast.info("Find or create an application in Step 1 first.");
                                                    return;
                                                }
                                                setStep(key);
                                            }}
                                            style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
                                        >
                                            {label}
                                            {doneSteps[key] && (
                                                <span className="text-success ml-2" title="Saved">&#10003;</span>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                );
                            })}
                        </Nav>
                        {applicationId && (
                            <div className="mt-4 text-muted font-size-sm">
                                Application: <code>{applicationId}</code>
                                {selectedUser?.email && <div className="mt-1">Customer: {selectedUser.email}</div>}
                            </div>
                        )}
                    </Col>
                    <Col lg={9}>
                        {jumpToNextStep && applicationId && (
                            <EmptyState>Loading the saved application…</EmptyState>
                        )}
                        {!jumpToNextStep && step === "find" && (
                            <FindOrCreateStep
                                userGroup={userGroup}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                                applicationId={applicationId}
                                setApplicationId={setApplicationId}
                                onResumeApplication={resumeApplication}
                            />
                        )}
                        {!jumpToNextStep && step === "company" && <CompanyCacStep applicationId={applicationId} parties={parties} />}
                        {!jumpToNextStep && step === "parties" && <PartiesStep applicationId={applicationId} onPartiesChanged={setParties} />}
                        {!jumpToNextStep && step === "party-docs" && (
                            <PartyDocumentsStep applicationId={applicationId} userId={selectedUser?.userId} parties={parties} />
                        )}
                        {!jumpToNextStep && step === "org-docs" && <OrgDocumentsStep applicationId={applicationId} userId={selectedUser?.userId} />}
                        {!jumpToNextStep && step === "agreements" && <AgreementsStep applicationId={applicationId} />}
                        {!jumpToNextStep && step === "review" && <ReviewStep applicationId={applicationId} />}
                        {!jumpToNextStep && step === "w8bene" && <W8beneStep applicationId={applicationId} parties={parties} />}
                        {!jumpToNextStep && step === "create-account" && (
                            <CreateAccountStep applicationId={applicationId} />
                        )}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateCorporateApplicationWizard;
